import React, { useState, useEffect, useCallback } from "react";
import { Input, Select } from "antd";
import { UpdateUser, UserInfo } from "../../../../Auth/Helper";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import Back from "../../../../../Components/Back";
import SimpleBackdrop from "../../../../../Components/SimpleBackDrop";
import MyEditor from "../../../../../Components/TextEditor";
import { useCountryStateCityProvider } from "../../../../../Providers/Common/CountyStateCity";
import UserProfileAvatar from "../../../../../Components/UserProfileAvatar";

const userObj = {
  id: "",
  name: "",
  email: "",
  company: "",
  country: "",
  phone: "",
  industry: "",
  industryExperience: "",
  portfolio: "",
  address: "",
  about: "",
  state: "",
  businessType: "",
  website: "",
  linkedin: "",
  profilePhoto: "",
  city: "",
};

const { id, ...userObjForAttributes } = userObj;

export default function EditVendorProfile({ setIsProfileEditing }) {
  const navigate = useNavigate();
  const [Loading, SetLoading] = useState(false);
  const [vendor, setVendor] = useState({ ...userObj });
  const [updateCount, setUpdateCount] = useState(1);
  const [userAttributes, setUserAttributes] = useState({
    userId: "",
    ...userObjForAttributes,
  });
  const {
    countries,
    states,
    cities,
    getCountries,
    getStatesByCountry,
    getCitiesByStateAndCountry,
  } = useCountryStateCityProvider();

  useEffect(() => {
    async function fetchData() {
      UserInfo()
        .then((res) => {
          if (res && res.data.success) {
            const { firstName, lastName, about, username, ...rest } =
              res.data.result;
            const { id, ...restOfAttributes } = rest;

            setVendor({
              ...vendor,
              ...rest,
              name: `${firstName} ${lastName}`,
              firstName: firstName,
              lastName: lastName,
              username: username,
              about: about ?? " ",
            });

            setUserAttributes({
              ...userAttributes,
              ...restOfAttributes,
              userId: id,
              firstName: firstName,
              lastName: lastName,
              username: username,
              name: `${firstName} ${lastName}`,
              about: about ?? " ",
            });
            if (rest.country) getStatesByCountry(rest.country);
            if (rest.state && rest.country)
              getCitiesByStateAndCountry(rest.state, rest.country);
          }
        })
        .catch((err) => {
          console.log(err);
        });
    }

    fetchData();
    getCountries();
  }, [updateCount]);

  const updateUserInfo = async () => {
    SetLoading(true);
    const updatedUser = await UpdateUser(userAttributes);
    if (updatedUser) {
      SetLoading(false);
      Swal.fire({
        icon: "success",
        title: "Success",
        html: "User profile updated successfully<br />Click on OK to go back to dashboard",
        showCloseButton: true,
      }).then((val) => {
        if (val.isConfirmed) navigate(-1);
      });
    } else {
      SetLoading(false);
      Swal.fire({
        icon: "error",
        title: "Oops...",
        text: "Something went wrong!",
      });
    }
  };

  const updateAvatar = async (url) => {
    const updatedUser = await UpdateUser({
      keycloakId: vendor.keycloakId,
      profilePhoto: url,
    });
    setUpdateCount(updateCount+1);
    if (updatedUser) {
      setUserAttributes({
        ...userAttributes,
        profilePhoto: url,
      });

      Swal.fire({
        icon: "success",
        title: "Success",
        text: "Your profile picture has been updated",
      });
    } else {
      throw new Error("Something went wrong");
    }
  };

  const setEditorData = useCallback(
    (data) => setUserAttributes({ ...userAttributes, about: data }),
    [userAttributes]
  );

  return (
    <div className="bg-white p-4  shadow-md my-4 rounded-md">
      <SimpleBackdrop open={Loading} />
      <div>
        <span
          className="text-2xl"
          style={{
            color: "#139588",
          }}
        >
          <Back onClick={() => navigate(-1)} />
          Edit Profile
        </span>
        <hr className="mt-8" />
      </div>
      <UserProfileAvatar user={vendor} updateCount={updateCount} setUpdateCount={setUpdateCount} updateHandler={updateAvatar}/>
      <hr className="mt-8" />
      <div className="flex lg:flex-row flex-col lg:space-x-[17%]">
        <div>
          <div className="flex flex-col">
            <span className="my-2 text-gray-700">Company Name</span>
            <Input
              onChange={(e) => {
                setUserAttributes({
                  ...userAttributes,
                  company: e.target.value,
                });
              }}
              className="w-80"
              placeholder={"Enter Company Name"}
              value={userAttributes.company}
            />
          </div>
          <div className="flex flex-col">
            <span className="my-2 text-gray-700">Country</span>
            <Select
              showSearch
              optionFilterProp="children"
              filterOption={(input, option) =>
                (option?.label ?? "")
                  .toLowerCase()
                  .includes(input.toLowerCase())
              }
              onChange={(value, option) => {
                setUserAttributes({ ...userAttributes, country: value });
                getStatesByCountry(value);
              }}
              placeholder="Enter Country"
              options={countries.length > 0 && countries.map((country) => {
                return { value: country.iso2, label: country.name };
              })}
              value={userAttributes.country}
            />
          </div>
          <div className="flex flex-col">
            <span className="my-2 text-gray-700">City</span>
            <Select
              showSearch
              optionFilterProp="children"
              filterOption={(input, option) =>
                (option?.label ?? "")
                  .toLowerCase()
                  .includes(input.toLowerCase())
              }
              onChange={(value) => {
                setUserAttributes({
                  ...userAttributes,
                  city: value,
                });
              }}
              placeholder={
                vendor.city && vendor.city.length > 0
                  ? vendor.city
                  : "Enter City"
              }
              options={cities.map((individualstate) => {
                return {
                  value: individualstate.name,
                  label: individualstate.name,
                };
              })}
              value={userAttributes.city}
            />
          </div>
        </div>
        <div>
          <div className="flex flex-col">
            <span className="my-2 text-gray-700">Business Type</span>
            <Input
              onChange={(e) => {
                setUserAttributes({
                  ...userAttributes,
                  businessType: e.target.value,
                });
              }}
              placeholder={"Enter Business Type"}
              value={userAttributes.businessType}
              className="w-80"
            />
          </div>
          <div className="flex flex-col">
            <span className="my-2 text-gray-700">State</span>
            <Select
              showSearch
              optionFilterProp="children"
              filterOption={(input, option) =>
                (option?.label ?? "")
                  .toLowerCase()
                  .includes(input.toLowerCase())
              }
              onChange={(value) => {
                setUserAttributes({
                  ...userAttributes,
                  state: value,
                });
                getCitiesByStateAndCountry(value, userAttributes.country);
              }}
              placeholder={
                vendor.state && vendor.state[0].length > 0
                  ? vendor.state
                  : "Enter State"
              }
              options={states.length > 0 && states.map((individualstate) => {
                return {
                  value: individualstate.iso2,
                  label: individualstate.name,
                };
              })}
              value={userAttributes.state}
            />
          </div>
          <div className="flex flex-col">
            <span className="my-2 text-gray-700">Street</span>
            <Input
              onChange={(e) => {
                setUserAttributes({
                  ...userAttributes,
                  address: e.target.value,
                });
              }}
              placeholder={"Enter Street"}
              value={userAttributes.address}
            />
          </div>
        </div>
      </div>
      <h1 className="font-bold mt-7">Account Administrator</h1>
      <div className="flex lg:flex-row flex-col lg:space-x-[17%]">
        <div>
          <div className="flex flex-col">
            <span className="my-2 text-gray-700">Username</span>
            <Input
              onChange={(e) => {
                setUserAttributes({
                  ...userAttributes,
                  name: e.target.value,
                });
              }}
              className="w-80 border-none -ml-2"
              placeholder="Enter User Name"
              value={userAttributes.username}
            />
          </div>
          <div className="flex flex-col">
            <span className="my-2 text-gray-700">First Name</span>
            <Input
              readOnly
              className="w-80 border-none -ml-2"
              value={userAttributes.firstName}
            />
          </div>
          <div className="flex flex-col">
            <span className="my-2 text-gray-700">Contact Number</span>
            <Input
              onChange={(e) => {
                setUserAttributes({
                  ...userAttributes,
                  phone: e.target.value,
                });
              }}
              maxLength={13}
              className="w-80"
              placeholder="Enter Contact No"
              value={userAttributes.phone}
            />
          </div>
        </div>

        <div className="flex flex-col">
          <div className="flex flex-col">
            <span className="my-2 text-gray-700">Email ID</span>
            <Input
              onChange={(e) => {
                setUserAttributes({
                  ...userAttributes,
                  email: e.target.value,
                });
              }}
              className="w-80 border-none -ml-2"
              placeholder={"Enter Email"}
              value={userAttributes.email}
            />
          </div>
          <div className="flex flex-col">
            <span className="my-2 text-gray-700">Last Name</span>
            <Input
              readOnly
              className="w-80 border-none -ml-2"
              value={userAttributes.lastName}
            />
          </div>
        </div>
      </div>
      <div className="mt-4">
        <div className="mt-2">
          <div className="flex flex-col">
            <span className=" text-gray-700 my-2">Website</span>
            <Input
              onChange={(e) => {
                setUserAttributes({
                  ...userAttributes,
                  website: e.target.value,
                });
              }}
              placeholder={"Enter Website"}
              value={userAttributes.website}
            />
          </div>
          <div className="flex flex-col">
            <span className=" text-gray-700 my-2">LinkedIn</span>
            <Input
              onChange={(e) => {
                setUserAttributes({
                  ...userAttributes,
                  linkedin: e.target.value,
                });
              }}
              placeholder={"Enter Linkedin"}
              value={userAttributes.linkedin}
            />
          </div>
          <div className=" text-gray-700 my-2">About</div>
          {vendor.about && (
            <MyEditor updateRaw={setEditorData} html={vendor.about} />
          )}
        </div>
      </div>

      <div className="flex flex-row justify-end mt-4 space-x-4">
        <button
          onClick={() => {
            navigate(-1);
          }}
          className="sm:mt-0 mt-4 text-sm space-x-8 px-2"
          style={{
            height: "35px",
            background: "white",
            borderRadius: "5px",
            border: " 0.5px solid #000000",
          }}
        >
          Discard Changes
        </button>
        <button
          onClick={() => updateUserInfo()}
          className="sm:mt-0 mt-4 text-sm space-x-8 px-2"
          style={{
            height: "35px",
            background: "#139588",
            borderRadius: "5px",
            color: "white",
          }}
        >
          Save Changes
        </button>
      </div>
    </div>
  );
}
